import { useState, useCallback, useMemo } from 'react';
import { GridPaginationModel } from '@mui/x-data-grid';

/**
 * Custom hook to persist DataGrid pagination preferences per page
 * 
 * @param pageKey - Unique identifier for the page (e.g., 'taxManagement', 'accountList')
 * @param defaultPageSize - Default page size if no preference is stored (default: 25)
 * @returns [paginationModel, setPaginationModel] - Similar to useState
 * 
 * @example
 * const [paginationModel, setPaginationModel] = usePaginationPreference('taxManagement', 25);
 */
export const usePaginationPreference = (
  pageKey: string,
  defaultPageSize: number = 25
): [GridPaginationModel, (model: GridPaginationModel | ((prev: GridPaginationModel) => GridPaginationModel)) => void] => {
  const storageKey = useMemo(() => `dataGrid_pagination_${pageKey}`, [pageKey]);

  // Initialize state from localStorage or use defaults
  const [paginationModel, setPaginationModelState] = useState<GridPaginationModel>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          page: 0, // Always start at page 0
          pageSize: parsed.pageSize || defaultPageSize,
        };
      }
    } catch (error) {
      console.warn(`Failed to load pagination preference for ${pageKey}:`, error);
    }
    return {
      page: 0,
      pageSize: defaultPageSize,
    };
  });

  // Custom setter that also persists to localStorage
  const setPaginationModel = useCallback((modelOrUpdater: GridPaginationModel | ((prev: GridPaginationModel) => GridPaginationModel)) => {
    setPaginationModelState((prevModel) => {
      const newModel = typeof modelOrUpdater === 'function' ? modelOrUpdater(prevModel) : modelOrUpdater;
      
      try {
        // Only store pageSize, not the current page
        localStorage.setItem(
          storageKey,
          JSON.stringify({ pageSize: newModel.pageSize })
        );
      } catch (error) {
        console.warn(`Failed to save pagination preference for ${pageKey}:`, error);
      }
      
      return newModel;
    });
  }, [storageKey]);

  return [paginationModel, setPaginationModel];
};

export default usePaginationPreference;
